import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, RoleEnum } from '@anecdotes/auth-core';
import { FrameworksFacadeService, FrameworksFoldersFacadeService, FrameworkAndControlsAccessorByPemissionsService } from '@anecdotes/data-store';
import { NavigationModel, SubMenuPositionY, NavigationMenuAction } from '@anecdotes/ui/navigation-bar';
import { map, startWith } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import {
  AppEvidencePageRouteSegments,
  AppRoutes,
  AppTrustCenterPageRouteSegments,
} from '@anecdotes/configuration-data-access';
import { Pages } from '@anecdotes/user-management-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagsService } from '@anecdotes/feature-flags';
import { FrameworkFolder } from '@anecdotes/api-contracts';

@Component({
  selector: 'app-navbar-host',
  templateUrl: './navbar-host.component.html',
  styleUrls: ['./navbar-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarHostComponent implements OnInit {
  @HostBinding('class')
  private readonly classes = 'block min-w-15 w-15 min-h-full';

  frameworksMenu: NavigationMenuAction[] = [];
  routes: NavigationModel[];
  shouldDisplayNavBar$: Observable<boolean>;

  initialized: boolean;
  frameworkFolders: FrameworkFolder[];

  private orderedRoutes: { [key: string]: NavigationModel } = {
    dashboard: {
      id: 'dashboard',
      featureFlag: '@dashboard-page',
      key: 'navigation.dashboard',
      route: AppRoutes.Dashboard,
      icon: 'dashboard',
      iconColorMode: 'stroke',
    },
    frameworks: {
      id: 'frameworks',
      featureFlag: '@frameworks-page',
      key: 'navigation.frameworks',
      icon: 'frameworks',
      categoryId: 'continuous-monitoring',
      menuActions: this.frameworksMenu,
      menuPositionY: SubMenuPositionY.BELOW,
      iconColorMode: 'fill',
      classes: 'overflow-y-scroll max-h-108 max-w-85 fixed',
      dynamicSubmenuPosition: true,
      action: () => {
        if (this.featureFlagsService.isEnabledSync('@frameworks-folders-view')) {
          this.router.navigate([
            `/${AppRoutes.Frameworks}/${AppRoutes.FrameworkFolder.replace(':folderId', this.frameworkFolders[0].id)}`,
          ]);
        } else {
          this.router.navigate([`/${AppRoutes.Frameworks}/${AppRoutes.FrameworksLibrary}`]);
        }
      },
    },
    unifiedView: {
      id: 'unifiedView',
      featureFlag: '@unified-view-page',
      categoryId: 'continuous-monitoring',
      key: 'navigation.unifiedView',
      icon: 'apps',
      route: AppRoutes.UnifiedView,
      iconColorMode: 'fill',
    },
    requirementHub: {
      id: 'requirementHub',
      featureFlag: '@requirement-hub-page',
      categoryId: 'continuous-monitoring',
      key: 'navigation.requirementHub',
      route: AppRoutes.RequirementHub,
      icon: 'requirement',
      iconColorMode: 'fill',
    },
    plugins: {
      id: 'plugins',
      featureFlag: '@plugins-page',
      categoryId: 'data',
      key: Pages.Plugin.title,
      route: AppRoutes.Plugins,
      icon: 'plugins',
      iconColorMode: 'fill',
    },
    evidencePool: {
      id: 'evidencePool',
      featureFlag: '@evidence-pool-page',
      categoryId: 'data',
      key: 'navigation.evidencePool',
      route: `${AppRoutes.EvidencePool}/${AppEvidencePageRouteSegments.EvidencePool}`,
      icon: 'evidence-pool',
      iconColorMode: 'fill',
    },
    evidenceLab: {
      id: 'evidenceLab',
      featureFlag: '@evidence-lab-page',
      categoryId: 'data',
      key: 'navigation.evidenceLab',
      route: `${AppRoutes.EvidencePool}/${AppEvidencePageRouteSegments.Lab}`,
      icon: 'evidence-lab',
      iconColorMode: 'fill',
    },
    riskManagement: {
      id: 'riskManagement',
      featureFlag: '@risk-page',
      categoryId: 'grc-apps',
      key: Pages.Risk.title,
      route: AppRoutes.RiskManagement,
      icon: 'risk-management',
      iconColorMode: 'fill',
      inBeta: false,
    },
    policyManager: {
      id: 'policyManager',
      featureFlag: '@policy-page',
      categoryId: 'grc-apps',
      key: Pages.Policy.title,
      route: AppRoutes.PolicyManager,
      icon: 'policy-nav',
      iconColorMode: 'fill',
      inBeta: false,
    },
    userAccessReview: {
      id: 'userAccessReview',
      featureFlag: '@uar-page',
      categoryId: 'grc-apps',
      key: Pages.UAR.title,
      route: AppRoutes.UserAccessReview,
      icon: 'user-access-review',
      iconColorMode: 'fill',
      inBeta: false,
    },
    trustCenter: {
      id: 'trustCenter',
      featureFlag: '@trust-center',
      categoryId: 'grc-apps',
      icon: 'trust-center',
      iconColorMode: 'fill',
      key: Pages.TrustCenter.title,
      route: `${AppRoutes.TrustCenter}/${AppTrustCenterPageRouteSegments.ActivityDashboard}`,
    },
    playbook: {
      id: 'playbook',
      featureFlag: '@playbooks-page',
      categoryId: 'grc-apps',
      icon: 'playbook/play',
      key: Pages.Playbooks.title,
      route: 'playbook',
    },
  };

  currentUserRole: RoleEnum;

  constructor(
    private ngbTooltipConfig: NgbTooltipConfig,
    private router: Router,
    private frameworksFacadeService: FrameworksFacadeService,
    private userProvider: AuthService,
    private featureFlagsService: FeatureFlagsService,
    private destroyRef: DestroyRef,
    private cd: ChangeDetectorRef,
    private frameworksFoldersFacade: FrameworksFoldersFacadeService,
    private frameworksAndControlsAccessor: FrameworkAndControlsAccessorByPemissionsService,
  ) {}

  ngOnInit(): void {
    this.initializeValues();
    this.initializeListeners();
  }

  getNavigationRoutes(): NavigationModel[] {
    return Object.values(this.orderedRoutes).map((entry) => entry as NavigationModel);
  }

  private initializeValues(): void {
    this.ngbTooltipConfig.tooltipClass = 'default-tooltip';
    this.ngbTooltipConfig.placement = 'right';
    this.ngbTooltipConfig.triggers = 'hover';
    this.ngbTooltipConfig.autoClose = false;
    this.ngbTooltipConfig.container = 'body';

    this.shouldDisplayNavBar$ = this.userProvider
      .getUser()
      .pipe(map((userClaims) => !userClaims?.role?.includes(RoleEnum.Zone)));
  }

  private initializeListeners(): void {
    combineLatest([
      this.frameworksFoldersFacade.getAllFolders(),
      this.frameworksFacadeService.getFrameworksDictionaryObs(),
      this.frameworksAndControlsAccessor.getPermittedFrameworksIds().pipe(startWith([])),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([folders, frameworksDict, permittedFrameworksIds]) => {
        this.frameworkFolders = folders.filter((folder) => {
          if (this.featureFlagsService.isEnabledSync('@contributor-or-viewer')) {
            return !!folder.frameworks_list.filter((id) => permittedFrameworksIds.includes(id)).length;
          }

          return true;
        });
        this.frameworksMenu = [];

        if (this.frameworkFolders.length || Object.keys(frameworksDict).length) {
          if (this.featureFlagsService.isEnabledSync('@frameworks-library')) {
            this.frameworksMenu.push({
              translationKey: 'frameworksLibrary',
              action: () => this.navigateToFrameworksLibrary(),
              icon: 'frameworks',
            });
          }

          if (this.featureFlagsService.isEnabledSync('@frameworks-folders-view')) {
            this.frameworkFolders.forEach((folder) => {
              this.frameworksMenu.push({
                value: folder.name,
                icon: 'folder',
                hasTopSeparator: true,
                action: () => this.navigateToFrameworkFolderPage(folder.id),
              });

              folder.frameworks_list
                .filter(
                  (frameworkId) =>
                    (this.featureFlagsService.isEnabledSync('@framework-specific', { frameworkId }) &&
                      frameworkId in frameworksDict) ||
                    (this.featureFlagsService.isEnabledSync('@contributor-or-viewer') &&
                      permittedFrameworksIds.includes(frameworkId)),
                )
                .forEach((frameworkId) => {
                  const framework = frameworksDict[frameworkId];

                  this.frameworksMenu.push({
                    value: framework.framework_name,
                    icon: this.getFrameworkIconLink(framework.framework_icon_id ?? framework.framework_id),
                    action: () => {
                      if (this.featureFlagsService.isEnabledSync('@contributor-or-viewer')) {
                        this.navigateToControlsPage(framework.framework_id);
                      } else {
                        this.navigateToFrameworkManager(framework.framework_id);
                      }
                    },
                  });
                });
            });
          } else {
            Object.keys(frameworksDict)
              .filter(
                (frameworkId) =>
                  this.featureFlagsService.isEnabledSync('@framework-specific', { frameworkId }) &&
                  frameworkId in frameworksDict,
              )
              .forEach((frameworkId) => {
                const framework = frameworksDict[frameworkId];

                this.frameworksMenu.push({
                  value: framework.framework_name,
                  icon: this.getFrameworkIconLink(framework.framework_icon_id ?? framework.framework_id),
                  action: () => this.navigateToFrameworkManager(framework.framework_id),
                });
              });
          }
        }

        this.orderedRoutes.frameworks.menuActions = this.frameworksMenu;
        this.cd.detectChanges();
      });
  }

  private navigateToFrameworkManager(relativeRoute: string): void {
    this.router.navigate([`/${AppRoutes.Frameworks}/${relativeRoute}/${AppRoutes.FrameworkOverview}`]);
  }

  private navigateToControlsPage(relativeRoute: string): void {
    this.router.navigate([`/${AppRoutes.Frameworks}/${relativeRoute}/${AppRoutes.ControlsPage}`]);
  }

  private navigateToFrameworkFolderPage(folderId: string): void {
    this.router.navigate([`/${AppRoutes.Frameworks}/${AppRoutes.FrameworkFolder.replace(':folderId', folderId)}`]);
  }

  private navigateToFrameworksLibrary(): void {
    this.router.navigate([`/${AppRoutes.Frameworks}/${AppRoutes.FrameworksLibrary}`]);
  }

  private getFrameworkIconLink(frameworkId: string): string {
    return `frameworks/${frameworkId}`;
  }
}
